/**
 * Audiodatei fuer die Song-Analyse pruefen und dekodieren – im Browser,
 * nur im Arbeitsspeicher. Nichts wird gespeichert oder gesendet.
 *
 * Pruefung vor dem Dekodieren: Groesse und Datei-Signatur (die ersten
 * Bytes), nicht nur die Endung. Danach die Laenge.
 */
import { SAMPLE_RATE } from './dsp.ts';

export const MAX_BYTES = 50 * 1024 * 1024;
export const MAX_SECONDS = 15 * 60;
export const MIN_SECONDS = 5;

export type AudioKind = 'mp3' | 'wav' | 'flac' | 'ogg' | 'm4a' | 'aac' | 'aiff';

const ascii = (b: Uint8Array, at: number, text: string) => [...text].every((c, i) => b[at + i] === c.charCodeAt(0));

/** Erkennt das Format an den ersten Bytes; null = keine unterstuetzte Audiodatei. */
export function sniffAudio(head: Uint8Array): AudioKind | null {
  if (head.length < 12) return null;
  if (ascii(head, 0, 'RIFF') && ascii(head, 8, 'WAVE')) return 'wav';
  if (ascii(head, 0, 'fLaC')) return 'flac';
  if (ascii(head, 0, 'OggS')) return 'ogg';
  if (ascii(head, 0, 'FORM') && (ascii(head, 8, 'AIFF') || ascii(head, 8, 'AIFC'))) return 'aiff';
  if (ascii(head, 4, 'ftyp')) return 'm4a';
  if (ascii(head, 0, 'ID3')) return 'mp3';
  const b0 = head[0] as number;
  const b1 = head[1] as number;
  if (b0 === 0xff && (b1 & 0xf6) === 0xf0) return 'aac'; // ADTS
  if (b0 === 0xff && (b1 & 0xe0) === 0xe0) return 'mp3'; // MPEG-Frame
  return null;
}

export class AudioFileError extends Error {}

/** Groesse und Signatur pruefen. Wirft AudioFileError mit einem Satz fuer die Oberflaeche. */
export async function checkAudioFile(file: Blob): Promise<AudioKind> {
  if (file.size === 0) throw new AudioFileError('Die Datei ist leer.');
  if (file.size > MAX_BYTES) throw new AudioFileError('Die Datei ist größer als 50 MB.');
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const kind = sniffAudio(head);
  if (!kind) throw new AudioFileError('Das ist keine unterstützte Audiodatei (MP3, WAV, FLAC, AAC, M4A, OGG, AIFF).');
  return kind;
}

/** Mischt Kanaele zu Mono. */
export function toMono(channels: Float32Array[]): Float32Array {
  const n = channels[0]?.length ?? 0;
  if (channels.length === 1) return channels[0] as Float32Array;
  const out = new Float32Array(n);
  for (const ch of channels) for (let i = 0; i < n; i++) out[i] = (out[i] as number) + (ch[i] as number) / channels.length;
  return out;
}

/**
 * Dekodiert direkt auf 22 050 Hz (decodeAudioData rechnet auf die Rate des
 * Kontexts um) und mischt zu Mono.
 */
export async function decodeForAnalysis(file: Blob): Promise<Float32Array> {
  await checkAudioFile(file);
  const Offline = (globalThis as unknown as { OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext })
    .OfflineAudioContext ?? (globalThis as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (!Offline) throw new AudioFileError('Dieser Browser kann kein Audio dekodieren.');
  const ctx = new Offline(1, 1, SAMPLE_RATE);
  let buffer: AudioBuffer;
  try {
    buffer = await ctx.decodeAudioData(await file.arrayBuffer());
  } catch {
    throw new AudioFileError('Die Datei lässt sich nicht lesen – ist sie beschädigt?');
  }
  if (buffer.duration > MAX_SECONDS) throw new AudioFileError('Der Song ist länger als 15 Minuten.');
  if (buffer.duration < MIN_SECONDS) throw new AudioFileError('Die Aufnahme ist kürzer als 5 Sekunden.');
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i));
  // Kopie, damit der Puffer an den Worker uebertragen werden kann.
  return Float32Array.from(toMono(channels));
}

/** Dateiname ohne Endung – nur als Vorschlag fuer den Titel, wird nirgends sonst gespeichert. */
export function titleFromFileName(name: string): string {
  return name.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_]+/g, ' ').trim().slice(0, 120);
}
